import { DocumentMapper } from "@core/data/mappers/document.mapper";
import { IncDocument } from "@core/domain/models/inc-document.model";
import { Nullable } from "@core/domain/types/nullable.type";
import { CCAAEnumMapper } from "@entity/data/mappers/ccaa-enum.mapper";
import { CreateTravelExpenseBody } from "@project/data/dto/expenses/create-expense.body";
import { EditTravelExpenseBody } from "@project/data/dto/expenses/edit-expense.body";
import { ExpenseTypeEnumDto } from "@project/data/dto/expenses/expense-type.dto";
import { ExpenseDto } from "@project/data/dto/expenses/expenses.dto";
import { TravelDto } from "@project/data/dto/expenses/travel.dto";
import { DietTypeEnumMapper } from "@project/data/mappers/expenses/diet-type-enum.mapper";
import { ExpenseSubTypeEnumMapper } from "@project/data/mappers/expenses/expense-sub-type-enum.mapper";
import { TransportTypeEnumMapper } from "@project/data/mappers/expenses/transport-type-enum.mapper";
import { CreateTravelExpense } from "@project/domain/models/expenses/create-expense.model";
import { DietTypeEnum } from "@project/domain/models/expenses/diet-type.model";
import { EditTravelExpense } from "@project/domain/models/expenses/edit-expense.model";
import { ExpenseTypeEnum } from "@project/domain/models/expenses/expense-type.model";
import {
    ProjectExpenseInvoiceLine,
    TravelExpense,
} from "@project/domain/models/expenses/expense.model";
import { ExpenseSubTypeEnum } from "@project/domain/models/expenses/sub-type.model";
import { TransportTypeEnum } from "@project/domain/models/expenses/transport-type.model";
import { ExpenseTravelFormValuesValidated } from "@project/presentation/components/project-form/expenses/expenses-form/travels/expense-travel-form.component";
import { validateSync } from "class-validator";
import { inject, injectable } from "inversify";
import { DateTime } from "luxon";
import {
    mapProjectExpensePaymentInstallments,
    mapProjectExpensePaymentInstallmentsFromForm,
    mapProjectExpensePaymentInstallmentsToBody,
} from "./project-expense-payment-installment.mapper";

const maximumDecimalsNumber = 2;

@injectable()
export class TravelExpenseMapper {
    constructor(
        @inject(TransportTypeEnumMapper)
        private readonly transportTypeEnumMapper: TransportTypeEnumMapper,
        @inject(DietTypeEnumMapper)
        private readonly dietTypeEnumMapper: DietTypeEnumMapper,
        @inject(ExpenseSubTypeEnumMapper)
        private readonly expenseSubTypeEnumMapper: ExpenseSubTypeEnumMapper,
        @inject(CCAAEnumMapper)
        private readonly ccaaEnumMapper: CCAAEnumMapper,
        @inject(DocumentMapper)
        private readonly documentMapper: DocumentMapper,
    ) {}

    map(
        expenseDto: ExpenseDto,
        travelData: TravelDto,
    ): Nullable<TravelExpense> {
        const errors = validateSync(travelData);

        if (errors.length > 0) {
            console.error(errors);
            return null;
        }

        const transportType = this.transportTypeEnumMapper.map(
            travelData.transportation_method,
        );

        const subtype = this.expenseSubTypeEnumMapper.map(
            travelData.subtype_expense,
        );

        const dietType = travelData.diet_type
            ? this.dietTypeEnumMapper.map(travelData.diet_type)
            : null;

        const documents: IncDocument[] = [];
        if (expenseDto.documents_data) {
            expenseDto.documents_data.forEach((document) => {
                const documentMapped = this.documentMapper.map(document);
                if (documentMapped) {
                    documents.push(documentMapped);
                }
            });
        }
        const lines = travelData.lines?.reduce<ProjectExpenseInvoiceLine[]>(
            (acc, line) => {
                const lineIva = Number(line.iva);

                if (Number.isNaN(lineIva)) return acc;

                acc.push(
                    new ProjectExpenseInvoiceLine(
                        line.concept,
                        line.base,
                        lineIva,
                        line.total,
                        line.position,
                        line.id,
                    ),
                );

                return acc;
            },
            [],
        );
        const paymentInstallments = mapProjectExpensePaymentInstallments(
            travelData.payment_installments,
        );

        return new TravelExpense(
            expenseDto.id,
            "",
            subtype ?? ExpenseSubTypeEnum.NONE,
            "",
            travelData.identification_document,
            travelData.full_name,
            travelData.address,
            travelData.position_function,
            travelData.travel_destination,
            travelData.date_departure
                ? DateTime.fromISO(travelData.date_departure)
                : null,
            travelData.date_return
                ? DateTime.fromISO(travelData.date_return)
                : null,
            transportType,
            travelData.other_transport,
            travelData.amount ? parseFloat(travelData.amount) : null,
            travelData.kilometers ? parseFloat(travelData.kilometers) : null,
            travelData.cost_per_kilometer
                ? parseFloat(travelData.cost_per_kilometer)
                : null,
            // Campos específicos para ACCOMMODATION_MEALS
            travelData.position,
            travelData.travel,
            travelData.start_date
                ? DateTime.fromISO(travelData.start_date)
                : null,
            travelData.end_date ? DateTime.fromISO(travelData.end_date) : null,
            dietType,
            // Usar days según el tipo de dieta
            dietType === DietTypeEnum.ACCOMMODATION
                ? travelData.days_accommodation
                : dietType === DietTypeEnum.MEALS
                  ? travelData.days_meals
                  : travelData.days_accommodation, // fallback legacy
            // Usar expense_person_day según el tipo de dieta
            dietType === DietTypeEnum.ACCOMMODATION
                ? travelData.daily_expense_accommodation
                    ? parseFloat(travelData.daily_expense_accommodation)
                    : null
                : dietType === DietTypeEnum.MEALS
                  ? travelData.daily_expense_meals
                      ? parseFloat(travelData.daily_expense_meals)
                      : null
                  : travelData.daily_expense_accommodation
                    ? parseFloat(travelData.daily_expense_accommodation)
                    : travelData.expense_person_day
                      ? parseFloat(travelData.expense_person_day)
                      : null, // fallback legacy
            // Usar total_expense según el tipo de dieta
            dietType === DietTypeEnum.ACCOMMODATION
                ? travelData.total_expense_accommodation
                    ? parseFloat(travelData.total_expense_accommodation)
                    : null
                : dietType === DietTypeEnum.MEALS
                  ? travelData.total_expense_meals
                      ? parseFloat(travelData.total_expense_meals)
                      : null
                  : travelData.total_expense_accommodation
                    ? parseFloat(travelData.total_expense_accommodation)
                    : travelData.total_expense
                      ? parseFloat(travelData.total_expense)
                      : null, // fallback legacy
            travelData.project_allocation_percentage
                ? parseFloat(travelData.project_allocation_percentage)
                : null,
            travelData.amount_allocated_to_project
                ? parseFloat(travelData.amount_allocated_to_project)
                : null,
            travelData.payment_date
                ? DateTime.fromISO(travelData.payment_date)
                : null,
            travelData.travel_destination ?? "",
            documents,
            expenseDto.project,
            travelData.territory
                ? this.ccaaEnumMapper.map(travelData.territory)
                : null,
            expenseDto.project_finantial_entity,
            travelData.is_indirect_cost ?? undefined,
            lines,
            paymentInstallments,
        );
    }

    mapFromFormValues(
        createFormValues: ExpenseTravelFormValuesValidated,
        projectId: number,
        projectFinantialEntityId?: number | null,
    ): CreateTravelExpense {
        const lines = createFormValues.useInvoiceLineBreakdown
            ? createFormValues.invoiceLines.map(
                  (line) =>
                      new ProjectExpenseInvoiceLine(
                          line.concept,
                          line.base ?? 0,
                          line.iva ?? 0,
                          line.total ?? 0,
                          line.position,
                          line.id,
                      ),
              )
            : [];
        const paymentInstallments =
            mapProjectExpensePaymentInstallmentsFromForm(
                createFormValues.usePaymentInstallments,
                createFormValues.paymentInstallments,
            );

        return new CreateTravelExpense(
            projectId,
            ExpenseTypeEnum.TRAVEL,
            createFormValues.subTypeId ?? ExpenseSubTypeEnum.DISPLACEMENT,
            createFormValues.dni,
            createFormValues.name,
            createFormValues.address,
            createFormValues.position,
            createFormValues.concept ?? "",
            createFormValues.startDate,
            createFormValues.endDate,
            createFormValues.transport ?? TransportTypeEnum.NONE,
            createFormValues.otherTransport ?? "",
            createFormValues.totalExpense,
            createFormValues.kms,
            createFormValues.expensePerKms,
            // Campos específicos para ACCOMMODATION_MEALS (opcionales para DISPLACEMENT)
            null, // position
            null, // travel
            null, // startDate (para alojamiento)
            null, // endDate (para alojamiento)
            null, // diet
            null, // days
            null, // expensePersonDay
            null, // totalExpense (para alojamiento)
            createFormValues.percentageImputation,
            createFormValues.amountImputation,
            createFormValues.paymentDate,
            createFormValues.documents.map((document) => document.id),
            createFormValues.concept,
            paymentInstallments,
            createFormValues.territory ?? undefined,
            createFormValues.isIndirectCost ?? undefined,
            projectFinantialEntityId,
            lines,
        );
    }

    mapFromAccommodationFormValues(
        createFormValues: any, // ExpenseAccommodationFormValuesValidated
        projectId: number,
        projectFinantialEntityId?: number | null,
    ): CreateTravelExpense {
        const lines = createFormValues.useInvoiceLineBreakdown
            ? createFormValues.invoiceLines.map(
                  (line: ProjectExpenseInvoiceLine) =>
                      new ProjectExpenseInvoiceLine(
                          line.concept,
                          line.base,
                          line.iva,
                          line.total,
                          line.position,
                          line.id,
                      ),
              )
            : [];
        const paymentInstallments =
            mapProjectExpensePaymentInstallmentsFromForm(
                createFormValues.usePaymentInstallments,
                createFormValues.paymentInstallments,
            );

        return new CreateTravelExpense(
            projectId,
            ExpenseTypeEnum.TRAVEL,
            createFormValues.subTypeId ??
                ExpenseSubTypeEnum.ACCOMMODATION_MEALS,
            createFormValues.dni,
            createFormValues.name,
            createFormValues.address,
            createFormValues.position,
            createFormValues.concept ?? "",
            createFormValues.startDate,
            createFormValues.endDate,
            TransportTypeEnum.NONE, // transport - NONE es ahora válido en el backend
            "", // otherTransport
            createFormValues.totalExpense, // amount
            null, // kms
            null, // expensePerKms
            // Campos específicos para ACCOMMODATION_MEALS
            createFormValues.position, // position
            createFormValues.concept, // travel
            createFormValues.startDate, // startDate (para alojamiento)
            createFormValues.endDate, // endDate (para alojamiento)
            createFormValues.diet, // diet
            createFormValues.days, // days
            createFormValues.expensePersonDay, // expensePersonDay
            createFormValues.totalExpense, // totalExpense (para alojamiento)
            createFormValues.percentageImputation,
            createFormValues.amountImputation,
            createFormValues.paymentDate,
            createFormValues.documents.map((document: any) => document.id),
            createFormValues.concept,
            paymentInstallments,
            createFormValues.territory ?? undefined,
            createFormValues.isIndirectCost ?? undefined,
            projectFinantialEntityId,
            lines,
        );
    }

    mapFromFormEditValues(
        editFormValues: ExpenseTravelFormValuesValidated,
        expenseId: number,
        projectId: number,
        projectFinantialEntityId?: number | null,
    ): EditTravelExpense {
        const lines = editFormValues.useInvoiceLineBreakdown
            ? editFormValues.invoiceLines.map(
                  (line) =>
                      new ProjectExpenseInvoiceLine(
                          line.concept,
                          line.base ?? 0,
                          line.iva ?? 0,
                          line.total ?? 0,
                          line.position,
                          line.id,
                      ),
              )
            : [];
        const paymentInstallments =
            mapProjectExpensePaymentInstallmentsFromForm(
                editFormValues.usePaymentInstallments,
                editFormValues.paymentInstallments,
            );

        return new EditTravelExpense(
            expenseId,
            projectId,
            ExpenseTypeEnum.TRAVEL,
            editFormValues.subTypeId ?? ExpenseSubTypeEnum.DISPLACEMENT,
            editFormValues.dni,
            editFormValues.name,
            editFormValues.address,
            editFormValues.position,
            editFormValues.concept ?? "",
            editFormValues.startDate,
            editFormValues.endDate,
            editFormValues.transport ?? TransportTypeEnum.NONE,
            editFormValues.otherTransport ?? "",
            editFormValues.totalExpense,
            editFormValues.kms,
            editFormValues.expensePerKms,
            // Campos específicos para ACCOMMODATION_MEALS (opcionales para DISPLACEMENT)
            null, // position
            null, // travel
            null, // startDate (para alojamiento)
            null, // endDate (para alojamiento)
            null, // diet
            null, // days
            null, // expensePersonDay
            null, // totalExpense (para alojamiento)
            editFormValues.percentageImputation,
            editFormValues.amountImputation,
            editFormValues.paymentDate,
            editFormValues.documents.map((document) => document.id),
            editFormValues.concept,
            paymentInstallments,
            editFormValues.territory ?? undefined,
            editFormValues.isIndirectCost ?? undefined,
            projectFinantialEntityId,
            lines,
        );
    }

    mapFromAccommodationFormEditValues(
        editFormValues: any, // ExpenseAccommodationFormValuesValidated
        expenseId: number,
        projectId: number,
        projectFinantialEntityId?: number | null,
    ): EditTravelExpense {
        const lines = editFormValues.useInvoiceLineBreakdown
            ? editFormValues.invoiceLines.map(
                  (line: ProjectExpenseInvoiceLine) =>
                      new ProjectExpenseInvoiceLine(
                          line.concept,
                          line.base,
                          line.iva,
                          line.total,
                          line.position,
                          line.id,
                      ),
              )
            : [];
        const paymentInstallments =
            mapProjectExpensePaymentInstallmentsFromForm(
                editFormValues.usePaymentInstallments,
                editFormValues.paymentInstallments,
            );

        return new EditTravelExpense(
            expenseId,
            projectId,
            ExpenseTypeEnum.TRAVEL,
            editFormValues.subTypeId ?? ExpenseSubTypeEnum.ACCOMMODATION_MEALS,
            editFormValues.dni,
            editFormValues.name,
            editFormValues.address,
            editFormValues.position,
            editFormValues.concept ?? "",
            editFormValues.startDate,
            editFormValues.endDate,
            TransportTypeEnum.NONE, // transport - NONE es ahora válido en el backend
            "", // otherTransport
            editFormValues.totalExpense, // amount
            null, // kms
            null, // expensePerKms
            // Campos específicos para ACCOMMODATION_MEALS
            editFormValues.position, // position
            editFormValues.concept, // travel
            editFormValues.startDate, // startDate (para alojamiento)
            editFormValues.endDate, // endDate (para alojamiento)
            editFormValues.diet, // diet
            editFormValues.days, // days
            editFormValues.expensePersonDay, // expensePersonDay
            editFormValues.totalExpense, // totalExpense (para alojamiento)
            editFormValues.percentageImputation,
            editFormValues.amountImputation,
            editFormValues.paymentDate,
            editFormValues.documents.map((document: any) => document.id),
            editFormValues.concept,
            paymentInstallments,
            editFormValues.territory ?? undefined,
            editFormValues.isIndirectCost ?? undefined,
            projectFinantialEntityId,
            lines,
        );
    }

    mapToDto(createExpense: CreateTravelExpense): CreateTravelExpenseBody {
        const transport = createExpense.transport
            ? this.transportTypeEnumMapper.mapToDto(createExpense.transport)
            : this.transportTypeEnumMapper.mapToDto(TransportTypeEnum.NONE);

        const subtypeExpense = createExpense.subtypeExpense
            ? this.expenseSubTypeEnumMapper.mapToDto(
                  createExpense.subtypeExpense,
              )
            : this.expenseSubTypeEnumMapper.mapToDto(
                  ExpenseSubTypeEnum.DISPLACEMENT,
              );

        const percentageImputation =
            createExpense.percentageImputation?.toFixed(maximumDecimalsNumber);
        const paymentInstallments = mapProjectExpensePaymentInstallmentsToBody(
            createExpense.paymentInstallments,
        );

        return {
            project: createExpense.projectId,
            type_expense: ExpenseTypeEnumDto.TRAVEL,
            subtype_expense: subtypeExpense,
            amount_allocated_to_project: Number(
                createExpense.amountImputation?.toPrecision(
                    maximumDecimalsNumber,
                ),
            ),
            project_allocation_percentage: percentageImputation
                ? parseFloat(percentageImputation)
                : 0,
            documents: createExpense.documents,
            territory: createExpense.territory
                ? this.ccaaEnumMapper.mapToDto(createExpense.territory)
                : undefined,
            is_indirect_cost: createExpense.isIndirectCost ?? false,
            full_name: createExpense.name ?? "",
            identification_document: createExpense.dni ?? "",
            address: createExpense.address ?? "",
            position_function: createExpense.positionFunction ?? "",
            travel_destination: createExpense.travelDestination ?? "",
            transportation_method: transport,
            date_departure: createExpense.dateDeparture?.toISODate() ?? "",
            date_return: createExpense.dateReturn?.toISODate() ?? "",
            cost_per_kilometer:
                createExpense.costPerKilometer?.toString() ?? "0",
            kilometers: createExpense.kilometers?.toString() ?? "0",
            amount: createExpense.amount?.toString() ?? "0",
            payment_date: createExpense.paymentDate?.toISODate() ?? "",
            other_transport: createExpense.otherTransport ?? "",
            project_finantial_entity:
                createExpense.projectFinantialEntityId ?? null,
            // Incluir diet_type para gastos de alojamiento
            ...(createExpense.diet && {
                diet_type: this.dietTypeEnumMapper.mapToDto(createExpense.diet),
            }),
            // Campos específicos para ACCOMMODATION_MEALS según el tipo de dieta
            ...(createExpense.subtypeExpense ===
                ExpenseSubTypeEnum.ACCOMMODATION_MEALS &&
                createExpense.diet === DietTypeEnum.ACCOMMODATION && {
                    days_accommodation: createExpense.days ?? 0,
                    daily_expense_accommodation:
                        createExpense.expensePersonDay?.toString() ?? "0",
                    total_expense_accommodation:
                        createExpense.totalExpense?.toString() ?? "0",
                }),
            ...(createExpense.subtypeExpense ===
                ExpenseSubTypeEnum.ACCOMMODATION_MEALS &&
                createExpense.diet === DietTypeEnum.MEALS && {
                    days_meals: createExpense.days ?? 0,
                    daily_expense_meals:
                        createExpense.expensePersonDay?.toString() ?? "0",
                    total_expense_meals:
                        createExpense.totalExpense?.toString() ?? "0",
                }),
            lines: createExpense.lines?.map((line) => ({
                concept: line.concept,
                base: line.base,
                iva: line.iva,
                total: line.total,
                position: line.position,
                id: line.id,
            })),
            payment_installments: paymentInstallments,
        };
    }

    mapToEditDto(editExpense: EditTravelExpense): EditTravelExpenseBody {
        const transport = editExpense.transport
            ? this.transportTypeEnumMapper.mapToDto(editExpense.transport)
            : this.transportTypeEnumMapper.mapToDto(TransportTypeEnum.NONE);

        const subtypeExpense = editExpense.subtypeExpense
            ? this.expenseSubTypeEnumMapper.mapToDto(editExpense.subtypeExpense)
            : this.expenseSubTypeEnumMapper.mapToDto(
                  ExpenseSubTypeEnum.DISPLACEMENT,
              );

        const percentageImputation = editExpense.percentageImputation?.toFixed(
            maximumDecimalsNumber,
        );
        const paymentInstallments = mapProjectExpensePaymentInstallmentsToBody(
            editExpense.paymentInstallments,
        );

        return {
            id: editExpense.id,
            project: editExpense.projectId,
            type_expense: ExpenseTypeEnumDto.TRAVEL,
            subtype_expense: subtypeExpense,
            amount_allocated_to_project: editExpense.amountImputation ?? 0,
            project_allocation_percentage: percentageImputation
                ? parseFloat(percentageImputation)
                : 0,
            documents: editExpense.documents,
            territory: editExpense.territory
                ? this.ccaaEnumMapper.mapToDto(editExpense.territory)
                : undefined,
            is_indirect_cost: editExpense.isIndirectCost ?? false,
            full_name: editExpense.name ?? "",
            identification_document: editExpense.dni ?? "",
            address: editExpense.address ?? "",
            position_function: editExpense.positionFunction ?? "",
            travel_destination: editExpense.travelDestination ?? "",
            transportation_method: transport,
            date_departure: editExpense.dateDeparture?.toISODate() ?? "",
            date_return: editExpense.dateReturn?.toISODate() ?? "",
            cost_per_kilometer: editExpense.costPerKilometer?.toString() ?? "0",
            kilometers: editExpense.kilometers?.toString() ?? "0",
            amount: editExpense.amount?.toString() ?? "0",
            payment_date: editExpense.paymentDate?.toISODate() ?? "",
            other_transport: editExpense.otherTransport ?? "",
            project_finantial_entity:
                editExpense.projectFinantialEntityId ?? null,
            // Incluir diet_type para gastos de alojamiento
            ...(editExpense.diet && {
                diet_type: this.dietTypeEnumMapper.mapToDto(editExpense.diet),
            }),
            // Campos específicos para ACCOMMODATION_MEALS según el tipo de dieta
            ...(editExpense.subtypeExpense ===
                ExpenseSubTypeEnum.ACCOMMODATION_MEALS &&
                editExpense.diet === DietTypeEnum.ACCOMMODATION && {
                    days_accommodation: editExpense.days ?? 0,
                    daily_expense_accommodation:
                        editExpense.expensePersonDay?.toString() ?? "0",
                    total_expense_accommodation:
                        editExpense.totalExpense?.toString() ?? "0",
                }),
            ...(editExpense.subtypeExpense ===
                ExpenseSubTypeEnum.ACCOMMODATION_MEALS &&
                editExpense.diet === DietTypeEnum.MEALS && {
                    days_meals: editExpense.days ?? 0,
                    daily_expense_meals:
                        editExpense.expensePersonDay?.toString() ?? "0",
                    total_expense_meals:
                        editExpense.totalExpense?.toString() ?? "0",
                }),
            lines: editExpense.lines?.map((line) => ({
                concept: line.concept,
                base: line.base,
                iva: line.iva,
                total: line.total,
                position: line.position,
                id: line.id,
            })),
            payment_installments: paymentInstallments,
        };
    }
}
